import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useDeleteUser,
  useRequestPasswordReset,
  useRestoreUser,
  useSuspendUser,
} from "@/hooks/useInternalUsers";
import { useUser, useUsers } from "@/hooks/useUsers";
import { SupportNoteService } from "@/lib/api/support-note.service";
import { usePermission } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import {
  ageGroups,
  type AppUser,
  type MaskedAppUser,
  SubscriptionPlan,
  type SupportNote,
} from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Activity,
  AlertTriangle,
  Apple,
  ArrowLeft,
  ArrowUpDown,
  Bookmark,
  CheckCircle,
  Clock,
  Compass,
  CreditCard,
  Dumbbell,
  Eye,
  Filter,
  Flame,
  Heart,
  KeyRound,
  Link2,
  Moon,
  MoreHorizontal,
  Scale,
  Search,
  Shield,
  ShieldOff,
  Smartphone,
  Sparkles,
  StickyNote,
  Target,
  Trash2,
  User,
  UserCircle,
  Users as UsersIcon,
  Utensils,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { GetCountries } from "react-country-state-city";
import { useLocation, useRoute } from "wouter";

type UserListItem = AppUser | MaskedAppUser;

// Minimal shape of react-country-state-city's Country (not re-exported at root).
type CountryOption = { iso2: string; name: string };

function isFullUser(user: UserListItem): user is AppUser {
  return "email" in user && !("maskedEmail" in user);
}

function getDisplayEmail(user: UserListItem): string | null {
  if (isFullUser(user)) {
    return user.email;
  }
  return (user as MaskedAppUser).maskedEmail;
}

function getDisplayPhone(user: UserListItem): string | null {
  if (isFullUser(user)) {
    return user.phone;
  }
  return (user as MaskedAppUser).maskedPhone;
}

function titleCase(s: string | null | undefined): string {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function safeFormatDate(
  value: string | null | undefined,
  pattern: string,
  fallback = "—",
): string {
  if (!value) return fallback;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? fallback : format(d, pattern);
}

// Birth-day style fields are stored UTC-anchored ("YYYY-MM-DDT00:00:00Z" or
// "...T18:30:00Z" for IST). Render using UTC components so the displayed
// calendar date matches the user's stored date regardless of viewer timezone.
function safeFormatDateUTC(
  value: string | null | undefined,
  pattern: string,
  fallback = "—",
): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  const localFromUtc = new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  );
  return format(localFromUtc, pattern);
}

function formatBirthTime(
  value: string | null | undefined,
  fallback = "Not set",
): string {
  if (!value) return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const hm = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (hm) {
    const h = Number(hm[1]);
    const m = Number(hm[2]);
    if (h >= 0 && h < 24 && m >= 0 && m < 60) {
      const d = new Date();
      d.setHours(h, m, 0, 0);
      return format(d, "h:mm a");
    }
  }
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return fallback;
  // ISO datetime — read UTC components so the stored wall-clock time renders.
  const local = new Date();
  local.setHours(d.getUTCHours(), d.getUTCMinutes(), 0, 0);
  return format(local, "h:mm a");
}

// Suggestion time is a real wall-clock moment in the user's timezone — stored
// as UTC and rendered back in that timezone so admin sees what the user picked.
function formatSuggestionTime(
  value: string | null | undefined,
  tz: string | null | undefined,
  fallback = "Not set",
): string {
  if (!value) return fallback;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fallback;
  try {
    return new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz || "UTC",
    }).format(d);
  } catch {
    return fallback;
  }
}

const REGION_DISPLAY = (() => {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" });
  } catch {
    return null;
  }
})();

function countryName(code: string | null | undefined): string {
  if (!code) return "";
  const trimmed = code.trim();
  if (!trimmed) return "";
  // 2-letter ISO codes only — pass through longer values (already a name)
  if (/^[A-Za-z]{2}$/.test(trimmed) && REGION_DISPLAY) {
    try {
      return REGION_DISPLAY.of(trimmed.toUpperCase()) ?? trimmed;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}

function toSentenceCase(s: string | null | undefined): string {
  if (!s) return "";
  // Onboarding values are stored camelCase ("selfLove") — split before lowercasing.
  const lower = s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  singleFun: "Single, looking for fun",
  married: "Married",
  inRelationship: "In a relationship",
  singleDating: "Single, dating without intention",
  singleLooking: "Single, looking for a relationship",
  singleNotLooking: "Single, not looking for a relationship",
  healing: "Healing from past relationship",
};

function formatRelationshipStatus(value: string | null | undefined): string {
  if (!value) return "";
  if (RELATIONSHIP_LABELS[value]) return RELATIONSHIP_LABELS[value];
  // Already a localized label — pass through unchanged.
  return value;
}

const AGE_GROUP_RANGES: Record<string, { ageMin?: number; ageMax?: number }> = {
  "18-24": { ageMin: 18, ageMax: 24 },
  "25-34": { ageMin: 25, ageMax: 34 },
  "35-44": { ageMin: 35, ageMax: 44 },
  "45-54": { ageMin: 45, ageMax: 54 },
  "55+": { ageMin: 55 },
};

const planBadgeVariants: Record<
  SubscriptionPlan,
  "default" | "secondary" | "outline"
> = {
  free: "outline",
  trial: "secondary",
  paid: "default",
};

export default function Users() {
  const [, params] = useRoute("/users/:id");
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const canSuspend = usePermission("users", "suspend");
  const canPasswordReset = usePermission("users", "trigger_password_reset");
  const canDelete = usePermission("users", "delete_gdpr");
  const canAddNotes = usePermission("users", "add_support_notes");

  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState<string>("all");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [ageGroupFilter, setAgeGroupFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("lastActive");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const debouncedSearch = useDebounce(search, 300);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, countryFilter, ageGroupFilter, planFilter, sortBy]);

  // Full country list for the filter dropdown (page-derived set is empty for
  // most pages since the list endpoint is server-paginated).
  useEffect(() => {
    // Third-party CDN fetch; on failure the filter just lists no countries.
    GetCountries()
      .then((result) => setCountries(result))
      .catch(() => {});
  }, []);

  // Count active filters for badge display
  const activeFilterCount = [
    planFilter !== "all",
    countryFilter !== "all",
    ageGroupFilter !== "all",
  ].filter(Boolean).length;

  // Clear all filters function
  const clearAllFilters = () => {
    setPlanFilter("all");
    setCountryFilter("all");
    setAgeGroupFilter("all");
  };

  const [actionDialog, setActionDialog] = useState<{
    type: "suspend" | "restore" | "passwordReset" | "delete" | "addNote" | null;
    user: AppUser | null;
  }>({ type: null, user: null });
  const [actionReason, setActionReason] = useState("");

  const userId = params?.id;

  const ageRange =
    ageGroupFilter !== "all" ? AGE_GROUP_RANGES[ageGroupFilter] : undefined;

  const { data: usersResponse, isLoading: usersLoading } = useUsers({
    search: debouncedSearch || undefined,
    page: currentPage,
    limit: pageSize,
    country: countryFilter !== "all" ? countryFilter : undefined,
    ageMin: ageRange?.ageMin,
    ageMax: ageRange?.ageMax,
    plan: planFilter !== "all" ? planFilter : undefined,
    sortBy,
  });
  const users: UserListItem[] = (usersResponse?.users ?? []) as UserListItem[];

  const { data: selectedUser, isLoading: userLoading } = useUser(userId);

  const { data: notes = [] } = useQuery<SupportNote[]>({
    queryKey: ["/api/app-users", userId, "notes"],
    queryFn: () => SupportNoteService.list(userId!),
    enabled: !!userId,
  });

  const handleActionSuccess = () => {
    setActionDialog({ type: null, user: null });
    setActionReason("");
  };

  const suspendMutation = useSuspendUser(handleActionSuccess);
  const restoreMutation = useRestoreUser(handleActionSuccess);
  const passwordResetMutation = useRequestPasswordReset(handleActionSuccess);
  const deleteMutation = useDeleteUser(() => {
    handleActionSuccess();
    setLocation("/users");
  });

  const addNoteMutation = useMutation({
    mutationFn: ({ id, content }: { id: string; content: string }) =>
      SupportNoteService.add(id, content),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/app-users", userId, "notes"],
      });
      toast({ title: "Support note added" });
      setActionDialog({ type: null, user: null });
      setActionReason("");
    },
    onError: () => {
      toast({ title: "Failed to add note", variant: "destructive" });
    },
  });

  // Server handles search, filters, and sort. No client-side post-processing.
  const filteredUsers = users;

  const pageItems = filteredUsers;
  const totalItems = usersResponse?.pagination.total ?? filteredUsers.length;
  const totalPages = usersResponse?.pagination.totalPages ?? 1;
  const startIndex = totalItems === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex = Math.min(currentPage * pageSize, totalItems);
  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const actionDialogEl = (
    <ActionDialog
      dialog={actionDialog}
      reason={actionReason}
      onReasonChange={setActionReason}
      onClose={() => {
        setActionDialog({ type: null, user: null });
        setActionReason("");
      }}
      onConfirm={() => {
        if (!actionDialog.user) return;
        const targetUserId = actionDialog.user.id;
        switch (actionDialog.type) {
          case "suspend":
            suspendMutation.mutate({
              userId: targetUserId,
              reason: actionReason,
            });
            break;
          case "restore":
            restoreMutation.mutate({
              userId: targetUserId,
              reason: actionReason,
            });
            break;
          case "passwordReset":
            passwordResetMutation.mutate({
              email: actionDialog.user.email ?? "",
            });
            break;
          case "delete":
            deleteMutation.mutate({
              userId: targetUserId,
              reason: actionReason,
            });
            break;
          case "addNote":
            addNoteMutation.mutate({ id: targetUserId, content: actionReason });
            break;
        }
      }}
      isPending={
        suspendMutation.isPending ||
        restoreMutation.isPending ||
        passwordResetMutation.isPending ||
        deleteMutation.isPending ||
        addNoteMutation.isPending
      }
    />
  );

  if (userId && selectedUser) {
    return (
      <>
        <UserProfile
          user={selectedUser}
          notes={notes}
          isLoading={userLoading}
          onBack={() => setLocation("/users")}
          onAction={(type, user) => setActionDialog({ type, user })}
          canSuspend={canSuspend}
          canPasswordReset={canPasswordReset}
          canDelete={canDelete}
          canAddNotes={canAddNotes}
        />
        {actionDialogEl}
      </>
    );
  }

  return (
    <div className="flex-1 overflow-auto bg-background">
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-serif font-semibold tracking-tight">
            Community
          </h1>
          <p className="text-muted-foreground mt-1">
            Privacy-first engagement signals for your community
          </p>
        </div>

        <section>
          <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
            <div>
              <h2 className="text-xl font-serif font-medium text-foreground">
                All Members
              </h2>
              <p className="text-sm text-muted-foreground">
                {totalItems} {totalItems === 1 ? "member" : "members"} in your
                community
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  data-testid="input-search-users"
                  placeholder="Search members..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" data-testid="button-filters">
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                    {activeFilterCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-2 px-1.5 py-0.5 text-xs"
                      >
                        {activeFilterCount}
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" align="start">
                  <div className="space-y-4">
                    <div className="font-medium text-sm">Filter by</div>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">
                          Plan
                        </label>
                        <Select
                          value={planFilter}
                          onValueChange={setPlanFilter}
                        >
                          <SelectTrigger
                            className="w-full"
                            data-testid="select-plan-filter"
                          >
                            <SelectValue placeholder="All Plans" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Plans</SelectItem>
                            <SelectItem value="free">Free</SelectItem>
                            <SelectItem value="trial">Trial</SelectItem>
                            <SelectItem value="paid">Paid</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">
                          Country
                        </label>
                        <Select
                          value={countryFilter}
                          onValueChange={setCountryFilter}
                        >
                          <SelectTrigger
                            className="w-full"
                            data-testid="select-country-filter"
                          >
                            <SelectValue placeholder="All Countries" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Countries</SelectItem>
                            {countries.map((country) => (
                              <SelectItem
                                key={country.iso2}
                                value={country.iso2}
                              >
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1.5 block">
                          Age Group
                        </label>
                        <Select
                          value={ageGroupFilter}
                          onValueChange={setAgeGroupFilter}
                        >
                          <SelectTrigger
                            className="w-full"
                            data-testid="select-age-filter"
                          >
                            <SelectValue placeholder="All Ages" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Ages</SelectItem>
                            {ageGroups.map((age) => (
                              <SelectItem key={age} value={age}>
                                {age}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {activeFilterCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full"
                        onClick={clearAllFilters}
                        data-testid="button-clear-filters"
                      >
                        Clear all filters
                      </Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px]" data-testid="select-sort">
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lastActive">Last Active</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="joined">Join Date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Active filter badges */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-sm text-muted-foreground">
                Active filters:
              </span>
              {planFilter !== "all" && (
                <Badge
                  variant="secondary"
                  className="gap-1"
                  data-testid="badge-filter-plan"
                >
                  {planFilter.charAt(0).toUpperCase() + planFilter.slice(1)}
                  <button
                    onClick={() => setPlanFilter("all")}
                    className="ml-1 hover:bg-muted rounded-full"
                    data-testid="button-remove-plan-filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {countryFilter !== "all" && (
                <Badge
                  variant="secondary"
                  className="gap-1"
                  data-testid="badge-filter-country"
                >
                  {countryName(countryFilter) || countryFilter}
                  <button
                    onClick={() => setCountryFilter("all")}
                    className="ml-1 hover:bg-muted rounded-full"
                    data-testid="button-remove-country-filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              {ageGroupFilter !== "all" && (
                <Badge
                  variant="secondary"
                  className="gap-1"
                  data-testid="badge-filter-age"
                >
                  {ageGroupFilter}
                  <button
                    onClick={() => setAgeGroupFilter("all")}
                    className="ml-1 hover:bg-muted rounded-full"
                    data-testid="button-remove-age-filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground"
                data-testid="button-clear-all"
              >
                Clear all
              </Button>
            </div>
          )}

          {usersLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <UsersIcon className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                  <p className="text-muted-foreground">No members found</p>
                </div>
              ) : (
                <>
                  {pageItems.map((user) => (
                    <Card
                      key={user.id}
                      className="hover-elevate transition-all duration-300 cursor-pointer"
                      onClick={() => setLocation(`/users/${user.id}`)}
                      data-testid={`row-user-${user.id}`}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                              <UserCircle className="w-5 h-5 text-muted-foreground" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">
                                {user.name || "Anonymous"}
                              </p>
                              <p className="text-sm text-muted-foreground truncate">
                                {getDisplayEmail(user) || "No email"}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-6 flex-wrap">
                            <Badge
                              variant={
                                user.plan
                                  ? planBadgeVariants[user.plan]
                                  : "outline"
                              }
                              className="capitalize"
                            >
                              {user.plan ?? "—"}
                              {user.plan === "trial" && user.trialEndDate && (
                                <span className="ml-1 text-xs opacity-75">
                                  (ends{" "}
                                  {safeFormatDate(user.trialEndDate, "MMM d")})
                                </span>
                              )}
                            </Badge>
                            <div className="text-left w-[100px] hidden sm:block">
                              <p className="text-xs text-muted-foreground">
                                Location
                              </p>
                              <p className="text-sm text-foreground truncate">
                                {countryName(user.country) || "Unknown"}
                              </p>
                            </div>
                            <div className="text-left w-[90px] hidden md:block">
                              <p className="text-xs text-muted-foreground">
                                Joined
                              </p>
                              <p className="text-sm text-foreground">
                                {safeFormatDate(user.createdAt, "MMM d, yyyy")}
                              </p>
                            </div>
                            <div className="text-left w-[90px] hidden lg:block">
                              <p className="text-xs text-muted-foreground">
                                Last Active
                              </p>
                              <p className="text-sm text-foreground">
                                {safeFormatDate(
                                  user.lastActiveAt,
                                  "MMM d, yyyy",
                                  "Never",
                                )}
                              </p>
                            </div>
                            <Button
                              size="icon"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLocation(`/users/${user.id}`);
                              }}
                              data-testid={`button-view-user-${user.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    startIndex={startIndex}
                    endIndex={endIndex}
                    canGoNext={canGoNext}
                    canGoPrev={canGoPrev}
                    onPageChange={setCurrentPage}
                    onNext={() => setCurrentPage((p) => p + 1)}
                    onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  />
                </>
              )}
            </div>
          )}
        </section>
      </div>

      {actionDialogEl}
    </div>
  );
}

function UserProfile({
  user,
  notes,
  isLoading,
  onBack,
  onAction,
  canSuspend,
  canPasswordReset,
  canDelete,
  canAddNotes,
}: {
  user: AppUser;
  notes: SupportNote[];
  isLoading: boolean;
  onBack: () => void;
  onAction: (type: any, user: AppUser) => void;
  canSuspend: boolean;
  canPasswordReset: boolean;
  canDelete: boolean;
  canAddNotes: boolean;
}) {
  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const profile: any = (user as any).userProfile ?? {};
  const u: any = user;
  const concentrationTopics: string[] =
    profile.concentrationTopics ?? u.concentrationTopics ?? [];
  const feelingCalledTo: string[] =
    profile.feelingCalledTo ?? u.feelingCalledTo ?? [];
  const contentInterests: string[] =
    profile.contentInterests ?? u.contentInterests ?? [];
  const religion = profile.religion ?? u.religion;
  const availableTime = profile.availableTime ?? u.availableTime;
  const relationshipStatus = profile.relationshipStatus ?? u.relationshipStatus;
  const hasChildren = profile.hasChildren ?? u.hasChildren;
  const birthDate = profile.birthDate ?? u.birthDate;
  const birthTime = profile.birthTime ?? u.birthTime;
  const birthCountry = profile.birthCountry ?? u.birthCountry ?? u.birthCity;
  const suggestionTime =
    profile.suggestionTime ?? u.suggestionTime ?? u.personalSuggestionsTime;

  const getInitials = (name: string | null) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <Button
        variant="ghost"
        size="sm"
        onClick={onBack}
        data-testid="button-back"
        className="mb-2"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Users
      </Button>

      <Card
        className="rounded-xl overflow-hidden"
        data-testid="card-profile-header"
      >
        <CardContent className="p-0">
          <div className="evo-gradient-subtle p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start gap-6">
              <div className="h-24 w-24 lg:h-28 lg:w-28 rounded-2xl bg-card flex items-center justify-center text-2xl lg:text-3xl font-semibold text-primary shadow-sm">
                {getInitials(user.name)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <h1
                      className="font-serif text-2xl lg:text-3xl font-semibold tracking-tight"
                      data-testid="text-user-name"
                    >
                      {user.name || "Anonymous User"}
                    </h1>
                    <p className="text-muted-foreground text-sm mt-1">
                      @{user.username || user.id}
                    </p>

                    <div className="flex flex-wrap items-center gap-2 mt-4">
                      <Badge
                        variant={
                          user.plan ? planBadgeVariants[user.plan] : "outline"
                        }
                        className="capitalize"
                        data-testid="badge-user-plan"
                      >
                        {user.plan ?? "—"}
                      </Badge>
                      <Badge
                        variant="outline"
                        className={
                          user.subscriptionStatus === "active"
                            ? "border-primary/30 bg-primary/5 text-primary"
                            : user.subscriptionStatus === "paused" ||
                                user.subscriptionStatus === "none"
                              ? "border-muted-foreground/30 bg-muted text-muted-foreground"
                              : "border-destructive/30 bg-destructive/5 text-destructive"
                        }
                        data-testid="badge-subscription-status"
                      >
                        {user.subscriptionStatus === "none"
                          ? "No subscription"
                          : titleCase(user.subscriptionStatus) || "—"}
                      </Badge>
                    </div>
                  </div>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        data-testid="button-user-actions"
                      >
                        Actions <MoreHorizontal className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      {canAddNotes && (
                        <DropdownMenuItem
                          onClick={() => onAction("addNote", user)}
                          data-testid="menu-item-add-note"
                        >
                          <StickyNote className="mr-2 h-4 w-4" />
                          Add Note
                        </DropdownMenuItem>
                      )}
                      {canPasswordReset && (
                        <DropdownMenuItem
                          onClick={() => onAction("passwordReset", user)}
                          data-testid="menu-item-reset-password"
                        >
                          <KeyRound className="mr-2 h-4 w-4" />
                          Reset Password
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      {canSuspend && !user.isSuspended && (
                        <DropdownMenuItem
                          onClick={() => onAction("suspend", user)}
                          className="text-destructive focus:text-destructive"
                          data-testid="menu-item-suspend"
                        >
                          <Shield className="mr-2 h-4 w-4" />
                          Suspend Account
                        </DropdownMenuItem>
                      )}
                      {canSuspend && user.isSuspended && (
                        <DropdownMenuItem
                          onClick={() => onAction("restore", user)}
                          data-testid="menu-item-restore"
                        >
                          <ShieldOff className="mr-2 h-4 w-4" />
                          Restore Account
                        </DropdownMenuItem>
                      )}
                      {canDelete && (
                        <DropdownMenuItem
                          onClick={() => onAction("delete", user)}
                          className="text-destructive focus:text-destructive"
                          data-testid="menu-item-delete"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete User (GDPR)
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {user.isSuspended && (
        <Card className="border-destructive/50 bg-destructive/5 rounded-xl">
          <CardContent className="flex items-center gap-4 py-5 px-6">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="font-medium text-destructive">Account Suspended</p>
              <p className="text-sm text-muted-foreground">
                Reason: {user.suspendedReason ?? "—"} | Suspended on:{" "}
                {safeFormatDate(user.suspendedAt, "MMM d, yyyy")}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div
        className="grid gap-4 grid-cols-2 lg:grid-cols-4"
        data-testid="section-quick-stats"
      >
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p
                  className="text-2xl font-semibold"
                  data-testid="text-sessions-count"
                >
                  {user.totalSessionsCompleted}
                </p>
                <p className="text-xs text-muted-foreground">
                  Content Completed
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-accent flex items-center justify-center">
                <Target className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p
                  className="text-2xl font-semibold"
                  data-testid="text-ritual-days"
                >
                  {user.qualifiedDays ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Ritual Days</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center">
                <Compass className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p
                  className="text-2xl font-semibold"
                  data-testid="text-completed-journeys"
                >
                  {user.completedJourneys ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Journeys</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-muted flex items-center justify-center">
                <Flame className="h-5 w-5 text-primary" />
              </div>
              <div className="flex items-center gap-2">
                <p
                  className="text-2xl font-semibold"
                  data-testid="text-streak-days"
                >
                  {user.currentStreakDays}
                </p>
                <span className="text-xs text-muted-foreground">
                  day streak
                </span>
                {user.currentStreakActive && (
                  <CheckCircle className="h-4 w-4 text-primary" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <User className="h-4 w-4 text-muted-foreground" />
              </div>
              <CardTitle className="text-lg">Account Details</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Name
                </p>
                <p className="font-medium">{user.name || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Username
                </p>
                <p className="font-medium">{user.username || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Email
                </p>
                <p className="font-medium">{user.email || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Phone
                </p>
                <p className="font-medium">{user.phone || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Country
                </p>
                <p className="font-medium">
                  {countryName(user.country) || "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Language
                </p>
                <p className="font-medium">
                  {user.language ? user.language.toUpperCase() : "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Timezone
                </p>
                <p className="font-medium">{user.timezone || "Not set"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Created
                </p>
                <p className="font-medium">
                  {safeFormatDate(user.createdAt, "MMM d, yyyy", "Not set")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Last Active
                </p>
                <p className="font-medium">
                  {safeFormatDate(user.lastActiveAt, "MMM d, yyyy", "Never")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Subscription</CardTitle>
                <CardDescription className="text-xs">
                  Plan and billing details
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Current Plan
              </span>
              <Badge
                variant={planBadgeVariants[user.plan]}
                className="capitalize"
              >
                {user.plan}
              </Badge>
            </div>
            {user.plan === "trial" && user.trialEndDate && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Trial Ends
                </span>
                <span className="text-sm font-medium">
                  {safeFormatDate(user.trialEndDate, "MMM d, yyyy")}
                </span>
              </div>
            )}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Status</span>
              <Badge
                variant="secondary"
                className={
                  user.subscriptionStatus === "active"
                    ? "bg-primary/10 text-primary"
                    : user.subscriptionStatus === "paused" ||
                        user.subscriptionStatus === "none"
                      ? "bg-muted text-muted-foreground"
                      : "bg-destructive/10 text-destructive"
                }
              >
                {user.subscriptionStatus === "none"
                  ? "No subscription"
                  : titleCase(user.subscriptionStatus) || "—"}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <Heart className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Wellness Signals</CardTitle>
                <CardDescription className="text-xs">
                  Data availability (values never shown)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Heart className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Mood</span>
                <Badge
                  variant={user.moodDataAvailable ? "default" : "secondary"}
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.moodDataAvailable ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Activity className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Cycle</span>
                <Badge
                  variant={
                    user.cycleTrackingConnected ? "default" : "secondary"
                  }
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.cycleTrackingConnected ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Moon className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Sleep</span>
                <Badge
                  variant={user.sleepDataConnected ? "default" : "secondary"}
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.sleepDataConnected ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Dumbbell className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Activity</span>
                <Badge
                  variant={user.activityDataConnected ? "default" : "secondary"}
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.activityDataConnected ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Utensils className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Nutrition</span>
                <Badge
                  variant={user.nutritionDataPresent ? "default" : "secondary"}
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.nutritionDataPresent ? "Yes" : "No"}
                </Badge>
              </div>
              <div className="flex items-center gap-2 p-2 rounded-lg bg-muted/30">
                <Scale className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs">Weight</span>
                <Badge
                  variant={user.weightDataPresent ? "default" : "secondary"}
                  className="ml-auto text-xs px-1.5 py-0"
                >
                  {user.weightDataPresent ? "Yes" : "No"}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <Link2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Connected Apps</CardTitle>
                <CardDescription className="text-xs">
                  Health app integrations
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Apple className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Apple Health</span>
              </div>
              <Badge
                variant={user.appleHealthConnected ? "default" : "secondary"}
                className="text-xs"
              >
                {user.appleHealthConnected ? "Connected" : "Not Connected"}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
              <div className="flex items-center gap-2">
                <Smartphone className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Google Fit</span>
              </div>
              <Badge
                variant={user.googleFitConnected ? "default" : "secondary"}
                className="text-xs"
              >
                {user.googleFitConnected ? "Connected" : "Not Connected"}
              </Badge>
            </div>
            <div className="flex items-center justify-between pt-2 mt-1 border-t">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Last Sync</span>
              </div>
              <span className="text-sm text-muted-foreground">
                {safeFormatDate(
                  user.lastSyncTimestamp,
                  "MMM d, h:mm a",
                  "Never",
                )}
              </span>
            </div>
            {user.syncErrorState && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-destructive/10 text-destructive text-sm mt-2">
                <AlertTriangle className="h-4 w-4" />
                {user.syncErrorState}
              </div>
            )}
          </CardContent>
        </Card>

        <Card
          className="md:col-span-2 rounded-xl"
          data-testid="card-onboarding-info"
        >
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <Bookmark className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">
                  Onboarding Preferences
                </CardTitle>
                <CardDescription className="text-xs">
                  Information collected during signup
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid gap-5 md:grid-cols-3">
              <div className="p-4 rounded-xl bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                  Focus Areas
                </p>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="list-focus-areas"
                >
                  {concentrationTopics.length > 0 ? (
                    concentrationTopics.map((topic, idx) => (
                      <Badge
                        key={topic}
                        variant="secondary"
                        className="text-xs"
                        data-testid={`badge-focus-area-${idx}`}
                      >
                        {toSentenceCase(topic)}
                      </Badge>
                    ))
                  ) : (
                    <span
                      className="text-sm text-muted-foreground"
                      data-testid="text-focus-areas-empty"
                    >
                      Not set
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-xl bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                  Goals
                </p>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="list-goals"
                >
                  {feelingCalledTo.length > 0 ? (
                    feelingCalledTo.map((item, idx) => (
                      <Badge
                        key={item}
                        variant="secondary"
                        className="text-xs"
                        data-testid={`badge-goal-${idx}`}
                      >
                        {toSentenceCase(item)}
                      </Badge>
                    ))
                  ) : (
                    <span
                      className="text-sm text-muted-foreground"
                      data-testid="text-goals-empty"
                    >
                      Not set
                    </span>
                  )}
                </div>
              </div>

              <div className="p-4 rounded-xl bg-muted/30">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
                  Content Interests
                </p>
                <div
                  className="flex flex-wrap gap-1.5"
                  data-testid="list-content-interests"
                >
                  {contentInterests.length > 0 ? (
                    contentInterests.map((interest, idx) => (
                      <Badge
                        key={interest}
                        variant="secondary"
                        className="text-xs"
                        data-testid={`badge-interest-${idx}`}
                      >
                        {toSentenceCase(interest)}
                      </Badge>
                    ))
                  ) : (
                    <span
                      className="text-sm text-muted-foreground"
                      data-testid="text-interests-empty"
                    >
                      Not set
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 lg:grid-cols-4 pt-2">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Religion/Spirituality
                </p>
                <p className="font-medium text-sm" data-testid="text-religion">
                  {toSentenceCase(religion) || "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Session Length
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-session-length"
                >
                  {toSentenceCase(availableTime) || "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Relationship
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-relationship-status"
                >
                  {formatRelationshipStatus(relationshipStatus) || "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Children
                </p>
                <p className="font-medium text-sm" data-testid="text-children">
                  {toSentenceCase(hasChildren) || "Not set"}
                </p>
              </div>
            </div>

            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 lg:grid-cols-4 pt-2 border-t">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Birth Date
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-birth-date"
                >
                  {safeFormatDateUTC(birthDate, "MMM d, yyyy", "Not set")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Birth Time
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-birth-time"
                >
                  {formatBirthTime(birthTime, "Not set")}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Birth City
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-birth-country"
                >
                  {birthCountry || "Not set"}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  Suggestion Time
                </p>
                <p
                  className="font-medium text-sm"
                  data-testid="text-suggestion-time"
                >
                  {formatSuggestionTime(
                    suggestionTime,
                    user.timezone,
                    "Not set",
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="md:col-span-2 rounded-xl">
          <CardHeader className="flex flex-row items-center justify-between gap-4 pb-4">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-muted flex items-center justify-center">
                <StickyNote className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Support Notes</CardTitle>
                <CardDescription className="text-xs">
                  Private notes visible only to Admin & Operations
                </CardDescription>
              </div>
            </div>
            {canAddNotes && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onAction("addNote", user)}
                data-testid="button-add-note"
              >
                <StickyNote className="mr-2 h-4 w-4" />
                Add Note
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {notes.length === 0 ? (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
                  <StickyNote className="h-6 w-6 text-muted-foreground/50" />
                </div>
                <p className="text-sm text-muted-foreground">
                  No support notes for this user
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {notes.map((note) => (
                  <div key={note.id} className="p-4 rounded-xl bg-muted/30">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        {note.authorUsername}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {safeFormatDate(note.createdAt, "MMM d, yyyy h:mm a")}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed">{note.content}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ActionDialog({
  dialog,
  reason,
  onReasonChange,
  onClose,
  onConfirm,
  isPending,
}: {
  dialog: { type: string | null; user: AppUser | null };
  reason: string;
  onReasonChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  if (!dialog.type || !dialog.user) return null;

  const configs: Record<
    string,
    {
      title: string;
      description: string;
      confirmText: string;
      variant?: "destructive" | "default";
      showInput: boolean;
      inputLabel?: string;
      inputPlaceholder?: string;
      selectOptions?: { value: string; label: string }[];
    }
  > = {
    suspend: {
      title: "Suspend Account",
      description: `Are you sure you want to suspend ${dialog.user.name || "this user"}? They will be unable to access the app.`,
      confirmText: "Suspend Account",
      variant: "destructive",
      showInput: true,
      inputLabel: "Reason for suspension (required)",
      inputPlaceholder: "Enter the reason for this suspension...",
    },
    restore: {
      title: "Restore Account",
      description: `Are you sure you want to restore ${dialog.user.name || "this user"}'s account?`,
      confirmText: "Restore Account",
      variant: "default",
      showInput: true,
      inputLabel: "Restoration note (required)",
      inputPlaceholder: "Enter a note for this restoration...",
    },
    passwordReset: {
      title: "Trigger Password Reset",
      description: `This will send a password reset email to ${dialog.user.email || "the user"}. You will not see the new password.`,
      confirmText: "Send Reset Email",
      variant: "default",
      showInput: false,
    },
    delete: {
      title: "Delete User Data (GDPR)",
      description: `This action is irreversible. All user data will be permanently deleted. Only proceed for GDPR requests, legal compliance, or severe policy violations.`,
      confirmText: "Delete Permanently",
      variant: "destructive",
      showInput: true,
      inputLabel: "Deletion reason",
      selectOptions: [
        { value: "gdpr", label: "GDPR Request" },
        { value: "legal", label: "Legal Compliance" },
        { value: "policy_violation", label: "Policy Violation" },
      ],
    },
    addNote: {
      title: "Add Support Note",
      description:
        "This note is private and will only be visible to Admin and Operations staff.",
      confirmText: "Add Note",
      variant: "default",
      showInput: true,
      inputLabel: "Note content",
      inputPlaceholder: "Enter your support note...",
    },
  };

  const config = configs[dialog.type];
  if (!config) return null;

  const isValid = !config.showInput || reason.trim().length > 0;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{config.title}</DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>
        {config.showInput && (
          <div className="space-y-2">
            <label className="text-sm font-medium">{config.inputLabel}</label>
            {config.selectOptions ? (
              <Select value={reason} onValueChange={onReasonChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a reason" />
                </SelectTrigger>
                <SelectContent>
                  {config.selectOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Textarea
                value={reason}
                onChange={(e) => onReasonChange(e.target.value)}
                placeholder={config.inputPlaceholder}
                rows={3}
              />
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant={config.variant}
            onClick={onConfirm}
            disabled={isPending || !isValid}
          >
            {isPending ? "Processing..." : config.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
